import { ChangeDetectionStrategy, Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import {
  ChartData,
  ChartDataSets,
  ChartTooltipItem,
  ChartType,
  TimeScale,
  Point,
  ChartXAxe,
  ChartYAxe
} from 'chart.js';
import { cloneDeep, get, findIndex, findLastIndex } from 'lodash';
import * as chartjsPluginStyle from 'chartjs-plugin-style';
import * as chartjsPluginAnnotation from 'chartjs-plugin-annotation';
import * as chartjsPluginCrosshair from 'chartjs-plugin-crosshair';
import moment from 'moment';
import {
  CROSSHAIR_COLOR,
  GRIDLINE_COLOR,
  GRAY,
  RED,
  ZERO,
  RED_RGB_FORMATTED,
  TOOLTIP_STYLING,
  DEFAULT_CHART_OPTIONS,
  ALERT_ORANGE
} from './constants';
import { CENTRALE_SANS_BOOK } from '@ispm/shared/constants/fonts';
import { ChartOptionsExtended, ChartTooltipOptionsExtended, LineChartDataItem, DatasetsConfig } from './interfaces';
import { UIChart } from 'primeng/chart';
import { ChartUtils } from './ChartUtils';
import {DateRange} from "@ispm/shared/components/calendar-wrapper/DateRange";
const DATE_FORMAT = 'YYYY-MM-DD';

const DATASET_CONFIG_VALUES: ChartDataSets = {
  borderColor: GRAY,
  borderWidth: 2,
  lineTension: 0,
  fill: false,
  cubicInterpolationMode: 'monotone',
  pointBorderWidth: ZERO,
  pointHoverBorderWidth: ZERO,
  pointRadius: 4,
  pointHoverRadius: 6,
  pointHitRadius: 12,
  pointBackgroundColor: GRAY,
  pointHoverBackgroundColor: GRAY
};

const DATASET_CONFIG_MINVALUES: ChartDataSets = {
  backgroundColor: `rgba(${RED_RGB_FORMATTED}, 0.2)`,
  borderWidth: ZERO,
  pointRadius: 0,
  pointHoverBorderWidth: ZERO,
  pointHoverRadius: 0,
  pointHitRadius: 0,
  lineTension: 0,
  fill: 'origin',
  cubicInterpolationMode: 'monotone',
  hideInLegendAndTooltip: true,
  spanGaps: true
};

const DATASET_CONFIG_MAXVALUES = {
  ...DATASET_CONFIG_MINVALUES,
  fill: 'end'
};

export enum CHART_TYPE {
  LINEAR = 'linear',
  TIME = 'time',
  LINE = 'line'
}

const X_AXIS_MAIN_ID = 'x-axis-main';
const X_AXIS_CURRENT_ID = 'x-axis-current';
const Y_AXIS_MAIN_ID = 'y-axis-main';
export const DATASET_PRIMARY_ID = 'dataset-primary';
export const DATASET_SECONDARY_ID = 'dataset-secondary';
export const DATASET_MIN_ID = 'dataset-min';
export const DATASET_MAX_ID = 'dataset-max';

@Component({
  selector: 'gen-generic-line-chart',
  templateUrl: './generic-line-chart.component.html',
  styleUrls: ['./generic-line-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GenericLineChartComponent implements OnInit, OnChanges {

  @Input()
  public data: LineChartDataItem[];

  @Input()
  public current: string;

  @Input()
  public unit= '';

  @Input()
  public mini = false;

  @Input()
  public minDate: string;

  @Input()
  public maxDate: string;

  @Input()
  public focusDateRange: DateRange;

  @Input()
  public showLabels = true;

  @Input()
  public hideLinePoints = false;

  @Input()
  public showCrossHair = false;

  @Input()
  public crossHairGroup = 1;

  @Input()
  public chartXAxisType?: string = CHART_TYPE.TIME;

  @ViewChild('chartRef') chartRef: UIChart;

  public chartData: ChartData;
  public chartOptions: ChartOptionsExtended;
  public chartType: ChartType = CHART_TYPE.LINE;
  public chartPlugins = [chartjsPluginStyle, chartjsPluginAnnotation, chartjsPluginCrosshair];
  public datasetsValues: (number | Point)[];
  public datasetsUnits: string[];
  public primaryUnit: string;
  public noFocusData = false;

  public ngOnInit() {
    this.setChartConfig();
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.data) {
      this.setDatasets();
    }
    if (changes.current || changes.mini || changes.focusDateRange || changes.hideLinePoints || changes.chartXAxisType) {
      this.setChartConfig();
    }
  }

  private setDatasets() {
    if (!this.data) { return; }

    this.primaryUnit = ChartUtils.getPrimaryUnit(this.data);
    this.noFocusData = !ChartUtils.areSomeDatesInFocus(this.data, this.focusDateRange);

    const datasetsConfig: DatasetsConfig = {
      values: null,
      primaryDataset: null,
      minDataset: null,
      maxDataset: null,
      pointColors: null,
      labels: null,
      units: null
    };

    if (this.isXAxisLinearType()) {
      datasetsConfig.values = this.getAxisValues('value') as Point[];
      datasetsConfig.minDataset = this.getAxisValues('minValue') as Point[];
      datasetsConfig.maxDataset = this.getAxisValues('maxValue') as Point[];
      datasetsConfig.labels = this.data.map(d => d.date === undefined ? null : moment(d.date).unix());
      datasetsConfig.pointColors = datasetsConfig.values.map(
        (value: Point, i) => (
          value.y >= (datasetsConfig.minDataset[i] as Point).y &&
          value.y <= (datasetsConfig.maxDataset[i] as Point).y
        ) ? GRAY : RED
      );
    } else {
      datasetsConfig.values = this.data.map(d => d.value === undefined ? null : d.value);
      datasetsConfig.minDataset = this.data.map(d => d.minValue === undefined ? null : d.minValue);
      datasetsConfig.maxDataset = this.data.map(d => d.maxValue === undefined ? null : d.maxValue);
      datasetsConfig.labels = this.data.map(d => d.date === undefined ? null : d.date);
      datasetsConfig.pointColors = datasetsConfig.values.map(
        (value, i) => (
          value >= datasetsConfig.minDataset[i] &&
          value <= datasetsConfig.maxDataset[i]
        ) ? GRAY : RED
      );
    }

    datasetsConfig.units = this.data.map(item => item.unit);


    let pointRadius;
    let pointHoverRadius;

    if (datasetsConfig.values.length === 1) {
      pointRadius = this.mini ? 2 : DATASET_CONFIG_VALUES.pointRadius;
      pointHoverRadius = this.mini ? 2 : DATASET_CONFIG_VALUES.pointHoverRadius;
      if (!this.isXAxisLinearType()) {
        const nextDayDate = moment(datasetsConfig.labels[0], DATE_FORMAT).add(1, 'days').format(DATE_FORMAT);
        datasetsConfig.labels.push(nextDayDate);
        datasetsConfig.values.push(null);
        datasetsConfig.minDataset.push(datasetsConfig.minDataset[0]);
        datasetsConfig.maxDataset.push(datasetsConfig.maxDataset[0]);
        datasetsConfig.units.push(datasetsConfig.units[0]);
        datasetsConfig.pointColors.push(datasetsConfig.pointColors[0]);
      }
    } else {
      if (this.hideLinePoints && !this.focusDateRange) {
        pointRadius = 0;
        pointHoverRadius = this.mini ? 0 : DATASET_CONFIG_VALUES.pointHoverRadius;
      } else {
        pointRadius = this.mini ? 2 : DATASET_CONFIG_VALUES.pointRadius;
        pointHoverRadius = this.mini ? 2 : DATASET_CONFIG_VALUES.pointHoverRadius;
      }
    }

    this.parseDatasetsByUnits(datasetsConfig);
    this.expandChartWithMinMaxDates(datasetsConfig);

    this.datasetsUnits = cloneDeep(datasetsConfig.units);
    this.datasetsValues = cloneDeep(datasetsConfig.values);

    this.chartData = {
      labels: datasetsConfig.labels,
      datasets: [{
        ...DATASET_CONFIG_VALUES,
        pointRadius,
        pointHoverRadius,
        pointBackgroundColor: datasetsConfig.pointColors,
        pointHoverBackgroundColor: datasetsConfig.pointColors,
        data: datasetsConfig.primaryDataset as number[] | Point[],
        label: DATASET_PRIMARY_ID
      },
        {
          ...DATASET_CONFIG_MINVALUES,
          data: datasetsConfig.minDataset as number[] | Point[],
          label: DATASET_MIN_ID
        }, {
          ...DATASET_CONFIG_MAXVALUES,
          data: datasetsConfig.maxDataset as number[] | Point[],
          label: DATASET_MAX_ID
        }]
    };

    this.renderSecondaryDataset(datasetsConfig.secondaryDataset);
  }

  private renderSecondaryDataset(secondaryDataset: (number | Point)[]) {
    if (secondaryDataset) {
      setTimeout(() => {

        const yAxis: { min: number } = get(this.chartRef, `chart.scales.${Y_AXIS_MAIN_ID}`, { min: 0 });
        const secondaryValues = secondaryDataset.map(item => {
          if (item !== null) {
            if (typeof item === 'number') {
              item = yAxis.min;
            } else if (typeof item === 'object') {
              item.y = yAxis.min;
            }
          }
          return item;
        });

        this.chartData.datasets.push(
          {
            ...DATASET_CONFIG_VALUES,
            pointHoverRadius: this.mini ? DATASET_CONFIG_VALUES.pointRadius : DATASET_CONFIG_VALUES.pointHoverRadius,
            pointBackgroundColor: ALERT_ORANGE,
            pointHoverBackgroundColor: ALERT_ORANGE,
            borderColor: ALERT_ORANGE,
            showLine: false,
            pointStyle: 'triangle',
            data: secondaryValues as number[] | Point[],
            label: DATASET_SECONDARY_ID
          }
        );
        if (this.chartRef) {
          this.chartRef.refresh();
        }
      }, 0);
    }
  }

  private expandChartWithMinMaxDates(datasetsConfig: DatasetsConfig) {

    if (this.minDate || this.maxDate) {
      let minDateValue: string | number;
      let maxDateValue: string | number;

      let newFirstMinValue: number | Point;
      let newFirstMaxValue: number | Point;
      let newLastMinValue: number | Point;
      let newLastMaxValue: number | Point;

      if (this.isXAxisLinearType()) {
        minDateValue = moment(this.minDate).unix();
        maxDateValue = moment(this.maxDate).unix();

        newFirstMinValue = { x: minDateValue, y: (datasetsConfig.minDataset[0] as Point).y };
        newFirstMaxValue = { x: minDateValue, y: (datasetsConfig.maxDataset[0] as Point).y };
        newLastMinValue = { x: maxDateValue, y: (datasetsConfig.minDataset[datasetsConfig.minDataset.length - 1] as Point).y };
        newLastMaxValue = { x: maxDateValue, y: (datasetsConfig.maxDataset[datasetsConfig.maxDataset.length - 1] as Point).y };
      } else {
        minDateValue = this.minDate;
        maxDateValue = this.maxDate;

        newFirstMinValue = datasetsConfig.minDataset[0];
        newFirstMaxValue = datasetsConfig.maxDataset[0];
        newLastMinValue = datasetsConfig.minDataset[datasetsConfig.minDataset.length - 1];
        newLastMaxValue = datasetsConfig.maxDataset[datasetsConfig.maxDataset.length - 1];
      }

      if (this.minDate) {
        datasetsConfig.labels.unshift(minDateValue);
        datasetsConfig.values.unshift(null);
        datasetsConfig.primaryDataset.unshift(null);
        if (datasetsConfig.secondaryDataset) { datasetsConfig.secondaryDataset.unshift(null); }
        datasetsConfig.units.unshift(null);
        datasetsConfig.pointColors.unshift(GRAY);
        datasetsConfig.minDataset.unshift(newFirstMinValue);
        datasetsConfig.maxDataset.unshift(newFirstMaxValue);
      }

      if (this.maxDate) {
        datasetsConfig.labels.push(maxDateValue);
        datasetsConfig.values.push(null);
        datasetsConfig.primaryDataset.push(null);
        if (datasetsConfig.secondaryDataset) { datasetsConfig.secondaryDataset.push(null); }
        datasetsConfig.units.push(null);
        datasetsConfig.pointColors.push(GRAY);
        datasetsConfig.minDataset.push(newLastMinValue);
        datasetsConfig.maxDataset.push(newLastMaxValue);
      }
    }
  }

  private getAxisValues(value: keyof LineChartDataItem) {
    if (!this.data.length && (value === 'minValue' || value === 'maxValue')) {
      // Return empty graph
      return [{
        x: moment(value === 'minValue' ? this.minDate : this.maxDate).unix(),
        y: null
      }];
    }

    return this.data.map((d, index) => ({
      x: moment(this.data[index].date).unix(),
      y: d[value] === undefined ? null : d[value]
    }));
  }

  private setChartConfig() {
    this.chartOptions = {
      ...DEFAULT_CHART_OPTIONS,
      scales: this.getScalesConfig(),
      tooltips: this.getTooltipConfig(),
      annotation: this.getAnnotationConfig(),
      plugins: {
        crosshair: (this.getCrossHairConfig() as chartjsPluginCrosshair.CrosshairOptions)
      }
    };
  }

  private getScalesConfig(): TimeScale {
    const { minVal, maxVal, stepSize } = this.getXScaleLimitValues();

    const xAxisMainConfig: ChartXAxe = {
      id: X_AXIS_MAIN_ID,
      type: this.chartXAxisType,
      distribution: CHART_TYPE.LINEAR,
      time: {
        unit: 'day',
        displayFormats: {
          day: DATE_FORMAT
        }
      },
      ticks: {
        min: minVal,
        max: maxVal,
        display: !this.mini && this.showLabels,
        fontFamily: CENTRALE_SANS_BOOK,
        callback: this.filterXTicks.bind(this),
        maxRotation: 0,
        padding: 10,
        source: 'data',
        lineHeight: 1,
        stepSize: stepSize
      },
      gridLines: {
        display: false,
        tickMarkLength: 0
      }
    };

    const yAxisMainConfig: ChartYAxe = {
      id: Y_AXIS_MAIN_ID,
      ticks: {
        display: !this.mini,
        fontFamily: CENTRALE_SANS_BOOK,
        padding: 4,
        maxTicksLimit: 5
      },
      gridLines: {
        tickMarkLength: 4,
        color: GRIDLINE_COLOR,
        zeroLineColor: GRIDLINE_COLOR
      }
    };

    const xAxes: ChartXAxe[] = [];
    const yAxes: ChartYAxe[] = [yAxisMainConfig];

    if (this.current) {
      // when there are two X axes we add a smaller padding to the first one
      xAxisMainConfig.ticks.padding = 5;
      const currentXAxisConfig = cloneDeep(xAxisMainConfig);
      currentXAxisConfig.id = X_AXIS_CURRENT_ID;
      currentXAxisConfig.ticks.padding = 5;
      currentXAxisConfig.ticks.callback = this.filterCurrentTick.bind(this);
      xAxes.push(currentXAxisConfig);
    }

    xAxes.push(xAxisMainConfig);

    return {
      xAxes,
      yAxes
    };
  }

  private filterXTicks(value: number | string, index: number, values: number[] | string[]) {
    if (this.isXAxisLinearType()) {
      return moment(Number(value) * 1000).format('DD MMM, YY');
    } else if (index === 0 || index === (values.length - 1)) {
      return `${moment(value).format('DD MMM, YY')}`;
    } else {
      return undefined;
    }
  }

  private filterCurrentTick(value: number | string, index: number, values: number[] | string[]) {
    if (index === 0 || index === (values.length - 1)) {
      return undefined;
    } else if (value === this.current) {
      return `${moment(value).format('DD MMM, YY')}`;
    } else {
      return undefined;
    }
  }

  private getXScaleLimitValues() {
    let minVal: string | number;
    let maxVal: string | number;
    let stepSize: number;
    const min = this.focusDateRange && this.focusDateRange.min ? moment(this.focusDateRange.min).format(DATE_FORMAT) : this.minDate;
    const max = this.focusDateRange && this.focusDateRange.max ? moment(this.focusDateRange.max).format(DATE_FORMAT) : this.maxDate;
    if (this.isXAxisLinearType()) {
      minVal = moment(min).unix();
      maxVal = moment(max).unix();
      stepSize = (maxVal - minVal) / 4;
    } else {
      minVal = min;
      maxVal = max;
      stepSize = 0;
    }
    return { minVal, maxVal, stepSize };
  }

  private getTooltipConfig(): ChartTooltipOptionsExtended {
    return {
      ...TOOLTIP_STYLING,
      enabled: !this.mini,
      intersect: false,
      mode: this.isXAxisLinearType() ? 'x' : 'point',
      position: 'nearest',
      callbacks: {
        title: this.getTooltipTitle.bind(this),
        label: this.getTooltipBody.bind(this)
      }
    };
  }

  private getTooltipTitle(items: ChartTooltipItem[]) {
    return `${moment(this.isXAxisLinearType() ? Number(items[0].label) * 1000 : items[0].label).format('DD MMM, YYYY')}`;
  }

  private isXAxisLinearType(): boolean {
    return this.chartXAxisType === CHART_TYPE.LINEAR;
  }

  private getTooltipBody(item: ChartTooltipItem, data: ChartData) {
    const dataset = data.datasets[item.datasetIndex];

    if (dataset.label === DATASET_PRIMARY_ID || dataset.label === DATASET_SECONDARY_ID) {
      let value = this.datasetsValues[item.index];
      if (typeof value === 'object') {
        value = get(value, 'y');
      }
      return `${value} ${this.datasetsUnits[item.index]}`;
    } else {
      return undefined;
    }
  }

  private getCrossHairConfig(): chartjsPluginCrosshair.CrosshairOptions | false {
    if (!this.showCrossHair) {
      return false;
    }

    return {
      line: {
        color: CROSSHAIR_COLOR,
        width: 1
      },
      sync: {
        enabled: true,
        group: this.crossHairGroup,
        suppressTooltips: false
      },
      zoom: {
        enabled: false
      }
    };

  }

  private getAnnotationConfig(): chartjsPluginAnnotation.AnnotationConfig {
    if (!this.current) {
      return undefined;
    }

    return {
      drawTime: 'beforeDatasetsDraw',
      events: [],
      annotations: [
        {
          type: 'line',
          id: 'today',
          mode: 'vertical',
          scaleID: X_AXIS_CURRENT_ID,
          value: this.current,
          borderColor: CROSSHAIR_COLOR,
          borderWidth: 1
        }
      ]
    };
  }

  private parseDatasetsByUnits(datasetsConfig: DatasetsConfig) {
    if (this.primaryUnit) {
      const primaryDataset: (number | Point)[] = [];
      const secondaryDataset: (number | Point)[] = [];
      const minDataset: (number | Point)[] = [];
      const maxDataset: (number | Point)[] = [];

      datasetsConfig.units.forEach((unit, i) => {
        if (unit === this.primaryUnit) {
          primaryDataset.push(datasetsConfig.values[i]);
          secondaryDataset.push(null);
          minDataset.push(datasetsConfig.minDataset[i]);
          maxDataset.push(datasetsConfig.maxDataset[i]);
        } else {
          primaryDataset.push(null);
          secondaryDataset.push(datasetsConfig.values[i]);
          minDataset.push(null);
          maxDataset.push(null);
        }
      });

      if (minDataset[0] === null) {
        const firstPrimaryUnitIndex = findIndex(datasetsConfig.units, unit => unit === this.primaryUnit);
        minDataset[0] = minDataset[firstPrimaryUnitIndex];
        maxDataset[0] = maxDataset[firstPrimaryUnitIndex];
      }

      if (maxDataset[maxDataset.length - 1] === null) {
        const lastPrimaryUnitIndex = findLastIndex(datasetsConfig.units, unit => unit === this.primaryUnit);
        minDataset[maxDataset.length - 1] = minDataset[lastPrimaryUnitIndex];
        maxDataset[maxDataset.length - 1] = maxDataset[lastPrimaryUnitIndex];
      }

      datasetsConfig.primaryDataset = primaryDataset;
      datasetsConfig.secondaryDataset = secondaryDataset;
      datasetsConfig.minDataset = minDataset;
      datasetsConfig.maxDataset = maxDataset;
    } else {
      datasetsConfig.primaryDataset = cloneDeep(datasetsConfig.values);
    }
  }

}
